import { useCallback, useEffect, useRef, useState } from 'react';
import { sdk } from 'src/utilities/sdk';
import {
  BasicAccountBudgetGroupsRequest,
  ILineOfCreditResponseType,
} from 'src/types/LineOfCredit';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
  SdkErrorResponse,
} from 'src/utilities/notifications';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { IErrorResponse } from 'src/utilities/shared-types';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';
import { MEAL_CREDIT_ENTITY_NAME } from 'src/constants/mealCredit';

export type UseLocGroupsManagementProps = {
  action: 'add' | 'remove';
  corpId: string;
  loc: ILineOfCreditResponseType;
  onSuccess?: () => void;
  onError?: () => void;
};

type UseLocGroupsManagementReturn = {
  isLoading?: boolean;
  mutate: (groupsSelected: string[]) => void;
};

export const useLocGroupsManagement = ({
  action,
  corpId,
  loc,
  onSuccess,
  onError,
}: UseLocGroupsManagementProps): UseLocGroupsManagementReturn => {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const currentGroupsSelectedRef = useRef<string[]>(null);

  const [isLoading, setIsLoading] = useState<boolean>();

  const resetState = () => {
    setIsLoading(false);
    currentGroupsSelectedRef.current = null;
  };

  const handleSuccess = () => {
    const currentGroupsSelected = currentGroupsSelectedRef.current;
    const groupsWord = getSingularOrPlural(
      currentGroupsSelected?.length,
      'group'
    );

    let message;

    switch (action) {
      case 'add':
        message = `${currentGroupsSelected?.length} ${groupsWord} added to your ${MEAL_CREDIT_ENTITY_NAME.singular} successfully.`;
        break;

      case 'remove':
        message = `${currentGroupsSelected?.length} ${groupsWord} removed from the ${MEAL_CREDIT_ENTITY_NAME.singular}.`;
        break;

      default:
        break;
    }

    pushSuccess(message);

    onSuccess?.();
    resetState();
  };

  const handleError = (error?: IErrorResponse | SdkErrorResponse) => {
    let errorMessage;

    switch (action) {
      case 'add':
        errorMessage = 'There was a problem adding groups. Try again.';
        break;
      case 'remove':
        errorMessage = 'There was a problem removing groups. Try again.';
        break;
      default:
        break;
    }

    if (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: errorMessage,
      });
    } else {
      pushError(errorMessage);
    }

    onError?.();
    resetState();
  };

  const updateLocGroups = (body: BasicAccountBudgetGroupsRequest) =>
    sdk.http.put(`/corp-admin/${corpId}/budget/${loc.id}/groups`, body);

  const existingGroupIds = loc.tag_invites.map(({ id }) => id);

  const mutate = useCallback<UseLocGroupsManagementReturn['mutate']>(
    async (groupsSelected: string[]) => {
      try {
        setIsLoading(true);
        currentGroupsSelectedRef.current = groupsSelected;

        let body: BasicAccountBudgetGroupsRequest;
        const budgetAmount = loc.tag_invites?.[0]?.budget;

        switch (action) {
          case 'add':
            body = {
              budget_amount: budgetAmount,
              group_ids: existingGroupIds.concat(groupsSelected),
            };
            break;
          case 'remove':
            body = {
              budget_amount: budgetAmount,
              group_ids: existingGroupIds.filter(
                (id) => !groupsSelected.includes(id)
              ),
            };
            break;
          default:
            break;
        }

        await startAsyncTask(() => {
          return updateLocGroups(body);
        });
      } catch (error) {
        handleError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loc.tag_invites, existingGroupIds]
  );

  const handleAsyncStatus = useCallback(() => {
    switch (asyncTaskStatus) {
      case AsyncTaskStatus.COMPLETE:
        /**
         * The reason for this timeout is that elastic search needs some time to rearrange the indexes
         * after a record is deleted. Without this timeout the search could potentially
         * return outdated results
         */
        setTimeout(() => {
          handleSuccess();
        }, LONG_SEARCH_DELAY);
        break;

      case AsyncTaskStatus.ERROR:
        handleError();
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, asyncTaskStatus]);

  useEffect(() => {
    handleAsyncStatus();
  }, [handleAsyncStatus]);

  return {
    isLoading,
    mutate,
  };
};
